import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { GenericCommandResult, GenericResult } from '../../pages/enrollment/enrollment';
import { AuthService } from '../services/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  // AD lookup (/users/me, including any suffix) must never go through the token/refresh flow
  const isMe = /\/users\/me(\/|\?|$)/.test(req.url);
  if (req.url.includes('/login') || req.url.includes('/refresh') || isMe && !req.url.includes('metrics')) {
    return next(req);
  }

  const token = authService.getJwtToken();
  if (!token) {
    return next(req);
  }

  const authReq = addToken(req, authService);

  return next(authReq).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(req, next, authService);
      }

      if (error instanceof HttpErrorResponse && error.status === 422) {
        return throwError(() => error);
      }

      const errorReturn: GenericResult = {
        success: false,
        message: error.error?.detail || error.error?.message || 'Non Authentication Error',
        data: error.error?.data
      };
      return throwError(() => errorReturn);
    })
  );
};

function addToken(request: HttpRequest<any>, authService: AuthService): HttpRequest<any> {
  const token: string = authService.getJwtToken();
  const filters: string = authService.getFilters();

  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      Filters: filters ?? ''
    }
  });
}

function handle401Error(request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((token: GenericCommandResult) => {
        isRefreshing = false;
        refreshTokenSubject.next(token.data);
        return next(addToken(request, authService));
      }),
      catchError(err => {
        isRefreshing = false;
        localStorage.clear();
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap(() => {
        return next(addToken(request, authService));
      })
    );
  }
};
